/**
 * Welcome-page template registry.
 *
 * Each template exposes a small set of design tokens that both the
 * public WelcomePage renderer and the admin editor's template-picker
 * cards read from. This keeps the templates DRY: to add a new one,
 * drop another entry here — no JSX branching needed.
 *
 * Design guidelines followed:
 *  • No `system-ui` as primary — every template picks a Google font
 *    from the approved list (Figtree / Fredoka / Playfair / Crimson /
 *    Montserrat).
 *  • Colour palettes are analogous / brand-derived (avoid clashing
 *    gradients per the "no blue→purple" guideline).
 *  • Every template supports light backgrounds; dark variants can be
 *    added later.
 */

// ------------------------------------------------------------------
// Colour helpers (hex → rgb / shade / rgba)
// ------------------------------------------------------------------
function parseHex(hex) {
  const h = (hex || '#00BCD4').replace('#', '').trim();
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return {
    r: parseInt(full.slice(0, 2), 16) || 0,
    g: parseInt(full.slice(2, 4), 16) || 0,
    b: parseInt(full.slice(4, 6), 16) || 0,
  };
}

export function rgba(hex, alpha = 1) {
  const { r, g, b } = parseHex(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

// pct in −100..100. Positive → toward white, negative → toward black.
export function shade(hex, pct) {
  const { r, g, b } = parseHex(hex);
  const target = pct >= 0 ? 255 : 0;
  const p = Math.abs(pct) / 100;
  const mix = (c) => Math.round(c + (target - c) * p);
  return `rgb(${mix(r)},${mix(g)},${mix(b)})`;
}

// ------------------------------------------------------------------
// Logo size scale — used by both top-bar and hero
// ------------------------------------------------------------------
export const LOGO_SIZES = {
  s: { label: 'Small',  topBar: 120, hero: 120, description: 'Compact' },
  m: { label: 'Medium', topBar: 180, hero: 180, description: 'Balanced (default)' },
  l: { label: 'Large',  topBar: 240, hero: 260, description: 'Bold, brand-forward' },
};

// ------------------------------------------------------------------
// Template registry
// ------------------------------------------------------------------
// Each factory takes the org's brand primary_color and returns the
// resolved token set. Doing it as a factory means the same template
// respects each org's palette.

const TEMPLATE_LIST = [
  {
    id: 'classic',
    name: 'Classic',
    tagline: 'Clean, minimal & timeless. A safe first choice.',
    swatch: ['#F7FAFC', '#00BCD4', '#334155'],
    build: (primary) => ({
      id: 'classic',
      primary,
      headlineFont: '"Figtree", system-ui, -apple-system, sans-serif',
      bodyFont: '"Figtree", system-ui, -apple-system, sans-serif',
      headlineWeight: 800,
      headlineTracking: '-0.02em',
      h2Weight: 700,
      pageBg: `linear-gradient(180deg, ${rgba(primary, 0.06)} 0%, #ffffff 40%)`,
      heroBg: `linear-gradient(180deg, ${rgba(primary, 0.10)}, ${rgba(primary, 0.02)})`,
      topBarBg: 'rgba(255,255,255,0.85)',
      cardBg: '#ffffff',
      cardBorder: '1px solid rgba(15,23,42,0.08)',
      cardAccentBar: false,
      showSectionDot: true,
      cardPadding: '1.4rem 1.5rem',
      text: '#1e293b',
      mutedText: '#64748b',
      border: 'rgba(15,23,42,0.10)',
      headlineColor: null,
      radius: 14,
      inputRadius: 10,
      inputBg: '#ffffff',
      ctaBg: primary,
      ctaFg: '#ffffff',
      ctaRadius: 10,
      ctaShadow: `0 6px 18px ${rgba(primary, 0.28)}`,
      shadow: '0 4px 14px rgba(15,23,42,0.06)',
      signupShadow: '0 12px 40px rgba(15,23,42,0.08)',
      heroLayout: 'band',
      dividers: null,
      showAccentDivider: false,
      showHeroBlob: false,
      grainOpacity: 0.02,
    }),
  },

  {
    id: 'playful',
    name: 'Playful Nursery',
    tagline: 'Rounded shapes, big friendly type & splash colours.',
    swatch: ['#FFF4E6', '#FF7A45', '#FDE68A'],
    build: (primary) => ({
      id: 'playful',
      primary,
      headlineFont: '"Fredoka", system-ui, sans-serif',
      bodyFont: '"Figtree", system-ui, sans-serif',
      headlineWeight: 700,
      headlineTracking: '-0.01em',
      h2Weight: 700,
      pageBg: `linear-gradient(180deg, ${rgba(primary, 0.13)} 0%, #FFFDF8 55%)`,
      heroBg: `linear-gradient(135deg, ${rgba(primary, 0.20)} 0%, ${rgba(shade(primary, 30), 0.35)} 100%)`,
      topBarBg: 'rgba(255,253,248,0.9)',
      cardBg: '#ffffff',
      cardBorder: 'none',
      cardAccentBar: false,
      showSectionDot: false,
      cardPadding: '1.6rem 1.75rem',
      text: '#2b1e0e',
      mutedText: '#78624a',
      border: 'rgba(120,98,74,0.20)',
      headlineColor: shade(primary, -35),
      radius: 26,
      inputRadius: 14,
      inputBg: '#FFFDF8',
      ctaBg: primary,
      ctaFg: '#ffffff',
      ctaRadius: 999,
      ctaShadow: `0 10px 24px ${rgba(primary, 0.38)}`,
      shadow: `0 10px 28px ${rgba(primary, 0.14)}`,
      signupShadow: `0 20px 44px ${rgba(primary, 0.20)}`,
      heroLayout: 'split',
      dividers: 'wavy',
      showAccentDivider: true,
      showHeroBlob: true,
      blobColor: rgba(shade(primary, 20), 0.55),
      grainOpacity: 0,
    }),
  },

  {
    id: 'bold',
    name: 'Bold Modern',
    tagline: 'Full-bleed hero, serif headlines, high-contrast.',
    swatch: ['#0F172A', '#F8FAFC', '#00BCD4'],
    build: (primary) => ({
      id: 'bold',
      primary,
      headlineFont: '"Playfair Display", Georgia, serif',
      bodyFont: '"Montserrat", system-ui, sans-serif',
      headlineWeight: 800,
      headlineTracking: '-0.015em',
      h2Weight: 700,
      pageBg: '#0f172a',
      heroBg: '#0f172a',
      topBarBg: 'rgba(15,23,42,0.9)',
      cardBg: '#111a2e',
      cardBorder: '1px solid rgba(255,255,255,0.08)',
      cardAccentBar: true,
      showSectionDot: false,
      cardPadding: '1.6rem 1.75rem 1.6rem 2rem',
      text: '#e2e8f0',
      mutedText: '#94a3b8',
      border: 'rgba(255,255,255,0.12)',
      headlineColor: '#f8fafc',
      radius: 4,
      inputRadius: 4,
      inputBg: '#1a2440',
      ctaBg: primary,
      ctaFg: '#0f172a',
      ctaRadius: 4,
      ctaShadow: `0 8px 24px ${rgba(primary, 0.35)}`,
      shadow: '0 12px 32px rgba(0,0,0,0.35)',
      signupShadow: '0 24px 60px rgba(0,0,0,0.45)',
      heroLayout: 'fullbleed',
      dividers: 'thin',
      showAccentDivider: true,
      showHeroBlob: false,
      grainOpacity: 0.05,
    }),
  },

  {
    id: 'warm',
    name: 'Warm & Homely',
    tagline: 'Cream backgrounds, elegant serif, gentle feel.',
    swatch: ['#FBF6EE', '#8B5A3C', '#D4A574'],
    build: (primary) => ({
      id: 'warm',
      primary,
      headlineFont: '"Crimson Text", Georgia, serif',
      bodyFont: '"Figtree", system-ui, sans-serif',
      headlineWeight: 700,
      headlineTracking: '-0.005em',
      h2Weight: 600,
      pageBg: 'linear-gradient(180deg, #FBF6EE 0%, #FDF9F2 100%)',
      heroBg: 'linear-gradient(180deg, #F5EBD9 0%, #FBF6EE 100%)',
      topBarBg: 'rgba(251,246,238,0.92)',
      cardBg: '#FFFCF6',
      cardBorder: '1px solid rgba(139,90,60,0.18)',
      cardAccentBar: false,
      showSectionDot: true,
      cardPadding: '1.6rem 1.75rem',
      text: '#3a2a1a',
      mutedText: '#7a5f45',
      border: 'rgba(139,90,60,0.20)',
      headlineColor: '#3a2a1a',
      radius: 18,
      inputRadius: 10,
      inputBg: '#FFFCF6',
      ctaBg: primary,
      ctaFg: '#ffffff',
      ctaRadius: 10,
      ctaShadow: `0 8px 22px ${rgba(primary, 0.30)}`,
      shadow: '0 6px 18px rgba(139,90,60,0.10)',
      signupShadow: '0 20px 44px rgba(139,90,60,0.18)',
      heroLayout: 'split',
      dividers: 'dots',
      showAccentDivider: true,
      showHeroBlob: false,
      grainOpacity: 0.03,
    }),
  },

  {
    id: 'minimal',
    name: 'Minimal Elegant',
    tagline: 'Whitespace-first, thin lines, monochrome with accent.',
    swatch: ['#FFFFFF', '#111827', '#00BCD4'],
    build: (primary) => ({
      id: 'minimal',
      primary,
      headlineFont: '"Montserrat", system-ui, sans-serif',
      bodyFont: '"Montserrat", system-ui, sans-serif',
      headlineWeight: 300,
      headlineTracking: '-0.03em',
      h2Weight: 400,
      pageBg: '#ffffff',
      heroBg: '#ffffff',
      topBarBg: 'rgba(255,255,255,0.92)',
      cardBg: '#ffffff',
      cardBorder: '1px solid rgba(17,24,39,0.10)',
      cardAccentBar: false,
      showSectionDot: false,
      cardPadding: '1.75rem 2rem',
      text: '#111827',
      mutedText: '#6b7280',
      border: 'rgba(17,24,39,0.10)',
      headlineColor: '#111827',
      radius: 6,
      inputRadius: 6,
      inputBg: '#ffffff',
      ctaBg: '#111827',
      ctaFg: '#ffffff',
      ctaRadius: 6,
      ctaShadow: 'none',
      shadow: 'none',
      signupShadow: '0 8px 24px rgba(17,24,39,0.08)',
      heroLayout: 'band',
      dividers: 'thin',
      showAccentDivider: true,
      showHeroBlob: false,
      grainOpacity: 0,
    }),
  },
];

// Public metadata (for the admin editor picker cards)
export const TEMPLATES = TEMPLATE_LIST.map(({ id, name, tagline, swatch }) => ({
  id, name, tagline, swatch,
}));

export function resolveTemplate(id, primaryColor) {
  const p = primaryColor || '#00BCD4';
  const found = TEMPLATE_LIST.find((t) => t.id === id) || TEMPLATE_LIST[0];
  return found.build(p);
}
